/**
 * SalonHub課金・アカウント管理サービス
 * @description サロンのSalonHubサービス利用料金・プラン管理
 */

import logger from '../utils/logger';
import type {
  SalonHubBilling,
  BillingRecord,
  PaymentMethod,
  PlanInfo,
  PlanChangeRequest,
  BillingApiResponse,
  UsageStats,
  UsageAlert
} from '../types/billing';
import { DEFAULT_PLAN_LIMITS, DEFAULT_PLAN_FEATURES, isUnlimited } from '../types/billing';
import { useStore } from '../store/useStore';

// 環境設定
const BILLING_API_BASE_URL = import.meta.env.VITE_BILLING_API_URL || 'https://api.salonhub.jp/billing/v1';

/**
 * SalonHub課金サービスクラス
 */
export class BillingService {
  private static apiKey: string | null = null;
  private static storeId: string | null = null;

  /**
   * 初期化
   */
  static initialize(apiKey: string, storeId: string) {
    this.apiKey = apiKey;
    this.storeId = storeId;
  }

  /**
   * 課金情報の取得
   */
  static async getBillingInfo(): Promise<SalonHubBilling> {
    try {
      // 本番実装時はAPI呼び出し
      if (import.meta.env.VITE_APP_ENV === 'production') {
        const response = await this.apiCall<SalonHubBilling>('GET', '/account/billing');
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error(response.error?.message || 'Failed to fetch billing info');
      }

      // 開発環境用モックデータ
      return this.getMockBillingInfo();
    } catch (error) {
      logger.error('Failed to get billing info:', error);
      throw error;
    }
  }

  /**
   * 請求履歴の取得
   */
  static async getBillingHistory(page = 1, limit = 10): Promise<BillingApiResponse<BillingRecord[]>> {
    try {
      if (import.meta.env.VITE_APP_ENV === 'production') {
        return await this.apiCall<BillingRecord[]>('GET', `/billing/history?page=${page}&limit=${limit}`);
      }

      // モック請求履歴
      return {
        success: true,
        data: this.getMockBillingHistory(),
        pagination: {
          page,
          limit,
          total: 12,
          hasMore: page * limit < 12,
        },
      };
    } catch (error) {
      logger.error('Failed to get billing history:', error);
      return {
        success: false,
        error: {
          code: 'BILLING_HISTORY_ERROR',
          message: error instanceof Error ? error.message : 'Unknown error',
        },
      };
    }
  }

  /**
   * 支払い方法の取得
   */
  static async getPaymentMethods(): Promise<PaymentMethod[]> {
    try {
      if (import.meta.env.VITE_APP_ENV === 'production') {
        const response = await this.apiCall<PaymentMethod[]>('GET', '/payment-methods');
        return response.data || [];
      }

      return this.getMockPaymentMethods();
    } catch (error) {
      logger.error('Failed to get payment methods:', error);
      return [];
    }
  }

  /**
   * 支払い方法の追加
   */
  static async addPaymentMethod(paymentMethod: Omit<PaymentMethod, 'id'>): Promise<PaymentMethod> {
    try {
      if (import.meta.env.VITE_APP_ENV === 'production') {
        const response = await this.apiCall<PaymentMethod>('POST', '/payment-methods', paymentMethod);
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error('Failed to add payment method');
      }

      // モック実装
      const newPaymentMethod: PaymentMethod = {
        ...paymentMethod,
        id: `pm_${Date.now()}`,
      };
      return newPaymentMethod;
    } catch (error) {
      logger.error('Failed to add payment method:', error);
      throw error;
    }
  }

  /**
   * デフォルト支払い方法の設定
   */
  static async setDefaultPaymentMethod(paymentMethodId: string): Promise<boolean> {
    try {
      if (import.meta.env.VITE_APP_ENV === 'production') {
        const response = await this.apiCall<{ success: boolean }>('PUT', `/payment-methods/${paymentMethodId}/default`);
        return response.success;
      }

      // モック実装
      logger.log('Set default payment method:', paymentMethodId);
      return true;
    } catch (error) {
      logger.error('Failed to set default payment method:', error);
      return false;
    }
  }

  /**
   * 利用可能プランの取得
   */
  static async getAvailablePlans(): Promise<PlanInfo[]> {
    try {
      if (import.meta.env.VITE_APP_ENV === 'production') {
        const response = await this.apiCall<PlanInfo[]>('GET', '/plans');
        return response.data || [];
      }

      return this.getMockPlans();
    } catch (error) {
      logger.error('Failed to get available plans:', error);
      return [];
    }
  }

  /**
   * プラン変更リクエスト
   */
  static async requestPlanChange(newPlanId: string, effectiveDate?: Date): Promise<PlanChangeRequest> {
    try {
      const requestData = {
        toPlan: newPlanId,
        effectiveDate: effectiveDate || new Date(),
      };

      if (import.meta.env.VITE_APP_ENV === 'production') {
        const response = await this.apiCall<PlanChangeRequest>('POST', '/plan-change', requestData);
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error('Failed to request plan change');
      }

      // モック実装
      const planChangeRequest: PlanChangeRequest = {
        id: `pcr_${Date.now()}`,
        fromPlan: 'basic',
        toPlan: newPlanId,
        requestedAt: new Date(),
        effectiveDate: effectiveDate || new Date(),
        status: 'pending',
      };

      return planChangeRequest;
    } catch (error) {
      logger.error('Failed to request plan change:', error);
      throw error;
    }
  }

  /**
   * 使用状況の取得
   */
  static async getUsageStats(): Promise<UsageStats> {
    try {
      if (import.meta.env.VITE_APP_ENV === 'production') {
        const response = await this.apiCall<UsageStats>('GET', '/usage');
        return response.data || this.getMockUsageStats();
      }

      return this.getMockUsageStats();
    } catch (error) {
      logger.error('Failed to get usage stats:', error);
      return this.getMockUsageStats();
    }
  }

  /**
   * 使用量アラートの取得
   */
  static async getUsageAlerts(): Promise<UsageAlert[]> {
    try {
      if (import.meta.env.VITE_APP_ENV === 'production') {
        const response = await this.apiCall<UsageAlert[]>('GET', '/usage/alerts');
        return response.data || [];
      }

      return this.getMockUsageAlerts();
    } catch (error) {
      logger.error('Failed to get usage alerts:', error);
      return [];
    }
  }

  /**
   * 請求書PDFのダウンロード
   */
  static async downloadInvoice(invoiceId: string): Promise<Blob> {
    try {
      if (import.meta.env.VITE_APP_ENV === 'production') {
        const response = await fetch(`${BILLING_API_BASE_URL}/invoices/${invoiceId}/pdf`, {
          headers: this.getAuthHeaders(),
        });
        return response.blob();
      }

      // モック PDF（空のBlob）
      return new Blob(['Mock PDF Content'], { type: 'application/pdf' });
    } catch (error) {
      logger.error('Failed to download invoice:', error);
      throw error;
    }
  }

  // ========================================
  // プライベートメソッド
  // ========================================

  /**
   * API呼び出しヘルパー
   */
  private static async apiCall<T>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    endpoint: string,
    data?: Record<string, unknown>
  ): Promise<BillingApiResponse<T>> {
    const url = `${BILLING_API_BASE_URL}${endpoint}`;
    
    const options: RequestInit = {
      method,
      headers: {
        ...this.getAuthHeaders(),
        'Content-Type': 'application/json',
      },
    };

    if (data) {
      options.body = JSON.stringify(data);
    }

    const response = await fetch(url, options);
    
    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * 認証ヘッダーの取得
   */
  private static getAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = {};
    
    if (this.apiKey) {
      headers['Authorization'] = `Bearer ${this.apiKey}`;
    }
    
    if (this.storeId) {
      headers['X-Store-ID'] = this.storeId;
    }
    
    return headers;
  }

  // ========================================
  // モックデータ
  // ========================================

  private static getMockBillingInfo(): SalonHubBilling {
    const now = new Date();
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);

    // useStoreから現在のプランを取得（旧形式のマッピング含む）
    const currentPlanState = useStore.getState().currentPlan;
    const currentPlanId = currentPlanState === 'basic' ? 'light' :
                          currentPlanState === 'enterprise' ? 'premium' :
                          currentPlanState as 'light' | 'standard' | 'premium';
    const currentPlan = this.getMockPlans().find(p => p.id === currentPlanId) || this.getMockPlans()[1]; // デフォルトはstandard

    return {
      account: {
        storeId: 'store_hitoyasumi_shibuya',
        storeName: 'ドライヘッドスパHITOYASUMI 渋谷店',
        contactEmail: 'owner@hitoyasumi.jp',
        billingEmail: 'billing@hitoyasumi.jp',
        address: {
          postalCode: '150-0043',
          prefecture: '東京都',
          city: '渋谷区',
          address1: '道玄坂1-2-3',
          building: '渋谷ビル4F',
        },
      },

      currentPlan,
      
      billing: {
        nextBillingDate: nextMonth,
        billingHistory: this.getMockBillingHistory(),
        paymentMethods: this.getMockPaymentMethods(),
        defaultPaymentMethod: 'pm_visa_1234',
      },
      
      usage: {
        currentMonth: this.getMockUsageStats(),
        previousMonth: this.getMockUsageStats(true),
        alerts: this.getMockUsageAlerts(),
      },
    };
  }

  private static getMockBillingHistory(): BillingRecord[] {
    const records: BillingRecord[] = [];
    const now = new Date();

    // 現在のプランを取得（旧形式のマッピング含む）
    const currentPlanState = useStore.getState().currentPlan;
    const currentPlanId = currentPlanState === 'basic' ? 'light' :
                          currentPlanState === 'enterprise' ? 'premium' :
                          currentPlanState as 'light' | 'standard' | 'premium';
    const currentPlan = this.getMockPlans().find(p => p.id === currentPlanId) || this.getMockPlans()[1];

    for (let i = 0; i < 6; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const amount = currentPlan.monthlyFee;
      const tax = Math.floor(amount * 0.1);

      records.push({
        id: `inv_${date.getTime()}`,
        invoiceNumber: `INV-${date.getFullYear()}${(date.getMonth() + 1).toString().padStart(2, '0')}-001`,
        billingDate: date,
        dueDate: new Date(date.getTime() + 14 * 24 * 60 * 60 * 1000), // 14日後
        amount,
        tax,
        totalAmount: amount + tax,
        status: i === 0 ? 'pending' : 'paid',
        paymentDate: i === 0 ? undefined : new Date(date.getTime() + 7 * 24 * 60 * 60 * 1000),
        paymentMethod: 'credit_card',
        items: [
          {
            id: `item_${date.getTime()}`,
            name: currentPlan.name,
            description: `${date.getFullYear()}年${date.getMonth() + 1}月分`,
            quantity: 1,
            unitPrice: amount,
            totalPrice: amount,
            period: {
              start: date,
              end: new Date(date.getFullYear(), date.getMonth() + 1, 0),
            },
          },
        ],
      });
    }

    return records;
  }

  private static getMockPaymentMethods(): PaymentMethod[] {
    return [
      {
        id: 'pm_visa_1234',
        type: 'credit_card',
        isDefault: true,
        isActive: true,
        card: {
          brand: 'visa',
          last4: '1234',
          expMonth: 12,
          expYear: 2026,
          holderName: 'SALON OWNER',
        },
      },
      {
        id: 'pm_master_5678',
        type: 'credit_card',
        isDefault: false,
        isActive: true,
        card: {
          brand: 'mastercard',
          last4: '5678',
          expMonth: 8,
          expYear: 2025,
          holderName: 'SALON OWNER',
        },
      },
    ];
  }

  private static getMockPlans(): PlanInfo[] {
    return [
      {
        id: 'light',
        name: 'SALONHUB ライトプラン',
        description: '小規模サロン向けの基本プラン',
        monthlyFee: 19800,
        yearlyFee: 213840,  // 10%割引
        billingCycle: 'monthly',
        features: DEFAULT_PLAN_FEATURES.light,
        limits: DEFAULT_PLAN_LIMITS.light,
        isActive: true,
      },
      {
        id: 'standard',
        name: 'SALONHUB スタンダードプラン',
        description: '成長中のサロン向け、AI分析機能付き',
        monthlyFee: 39800,
        yearlyFee: 429840,  // 10%割引
        billingCycle: 'monthly',
        features: DEFAULT_PLAN_FEATURES.standard,
        limits: DEFAULT_PLAN_LIMITS.standard,
        isActive: true,
      },
      {
        id: 'premium',
        name: 'SALONHUB プレミアムプラン',
        description: '大規模サロン・チェーン向け完全機能',
        monthlyFee: 79800,
        yearlyFee: 861840,  // 10%割引
        billingCycle: 'monthly',
        features: DEFAULT_PLAN_FEATURES.premium,
        limits: DEFAULT_PLAN_LIMITS.premium,
        isActive: true,
      },
    ];
  }

  private static getMockUsageStats(isPrevious = false): UsageStats {
    const now = new Date();
    const month = isPrevious ? now.getMonth() - 1 : now.getMonth();
    const year = month < 0 ? now.getFullYear() - 1 : now.getFullYear();
    const actualMonth = month < 0 ? 11 : month;

    const start = new Date(year, actualMonth, 1);
    const end = new Date(year, actualMonth + 1, 0);

    // 現在のプランの制限値を取得（旧形式のマッピング含む）
    const currentPlanState = useStore.getState().currentPlan;
    const currentPlanId = currentPlanState === 'basic' ? 'light' :
                          currentPlanState === 'enterprise' ? 'premium' :
                          currentPlanState as 'light' | 'standard' | 'premium';
    const limits = DEFAULT_PLAN_LIMITS[currentPlanId] || DEFAULT_PLAN_LIMITS.standard;
    
    const staffCount = isPrevious ? 6 : 8;
    const reservationCount = isPrevious ? 1200 : 1450;
    const customerCount = isPrevious ? 800 : 920;
    const storageUsedMB = isPrevious ? 12000 : 14500; // 14.5GB
    const aiAnalysisCount = isPrevious ? 45 : 67;
    
    return {
      period: { start, end },
      staffCount,
      reservationCount,
      customerCount,
      storageUsedMB,
      aiAnalysisCount,
      usage: {
        staffUsage: isUnlimited(limits.staffCount) ? 0 : staffCount / limits.staffCount,
        reservationUsage: isUnlimited(limits.reservationsPerMonth) ? 0 : reservationCount / limits.reservationsPerMonth,
        customerUsage: isUnlimited(limits.customersLimit) ? 0 : customerCount / limits.customersLimit,
        storageUsage: isUnlimited(limits.storageGB) ? 0 : storageUsedMB / (limits.storageGB * 1024), // MB to GB
        aiUsage: isUnlimited(limits.aiAnalysisCount) ? 0 : aiAnalysisCount / limits.aiAnalysisCount,
      },
    };
  }

  private static getMockUsageAlerts(): UsageAlert[] {
    const now = new Date();

    // AI分析の警告のみ（重要な制限）
    return [
      {
        id: 'alert_ai_limit',
        type: 'limit_reached',
        category: 'ai',
        message: 'AI分析の月間上限に達しました',
        threshold: 1.0,
        currentUsage: 100,
        limit: 100,
        createdAt: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000), // 2日前
        isRead: true,
      },
    ];
  }
}

export default BillingService;